import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AutomationDialog,
  type AutomationRow,
} from '@/components/messaging/automation-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { formatDateTime } from '@/utils/format';

export const MessageAutomationsPanel = () => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canManage = hasPermission(membership, PERMISSIONS.MESSAGING_MANAGE_AUTOMATIONS);

  const [configureTarget, setConfigureTarget] = useState<AutomationRow | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);

  const list = useQuery({
    queryKey: ['messaging', 'automations'],
    queryFn: () => api.get<{ data: AutomationRow[] }>('/messaging/automations'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      api.patch<AutomationRow>(`/messaging/automations/${id}`, { isEnabled }),
    onSuccess: (row) => {
      toast.success(row.isEnabled ? t('automations.enabled') : t('automations.disabled'));
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'automations'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const scheduleSummary = (row: AutomationRow): string => {
    if (row.type === 'service_reminder' || row.type === 'event_reminder') {
      return t('automations.scheduleHoursBefore', {
        hours: row.configuration.hoursBefore ?? 24,
      });
    }
    if (row.type === 'first_timer_welcome') {
      return t('automations.scheduleOnVisit');
    }
    if (row.type === 'contribution_receipt') {
      return t('automations.scheduleOnGift');
    }
    if (row.type === 'pledge_confirmation') {
      return t('automations.scheduleOnPledge');
    }
    if (row.type === 'devotional_publish') {
      return t('automations.scheduleOnPublish');
    }
    if (row.type === 'pledge_reminder') {
      return t('automations.scheduleDaily', { time: row.configuration.sendTime });
    }
    if (row.type === 'tithe_reminder') {
      return t('automations.scheduleWeekly', {
        weekday: t(`automations.weekdays.${row.configuration.weekday ?? 6}`),
        time: row.configuration.sendTime,
      });
    }
    if (row.type === 'absence_follow_up') {
      return t('automations.scheduleAbsence', {
        days: row.configuration.absenceDays ?? 21,
        time: row.configuration.sendTime,
      });
    }
    return t('automations.scheduleDaily', { time: row.configuration.sendTime });
  };

  const columns: DataTableColumn<AutomationRow>[] = [
    {
      id: 'name',
      header: t('automations.columns.name'),
      cell: (row) => (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span>{row.name}</span>
          </div>
          <p className="text-caption text-fg-secondary">
            {t(`automations.types.${row.type}.description`, {
              defaultValue: '',
            })}
          </p>
        </div>
      ),
    },
    {
      id: 'schedule',
      header: t('automations.columns.schedule'),
      cell: (row) => scheduleSummary(row),
      hideBelowMd: true,
    },
    {
      id: 'lastRun',
      header: t('automations.columns.lastRun'),
      cell: (row) => (row.lastRunAt ? formatDateTime(row.lastRunAt) : '—'),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('automations.columns.status'),
      cell: (row) => (
        <Switch
          checked={row.isEnabled}
          disabled={!canManage || !row.runnable || toggle.isPending}
          onCheckedChange={(checked) => {
            if (!row.runnable) return;
            toggle.mutate({ id: row.id, isEnabled: checked });
          }}
          aria-label={t('automations.toggleLabel', { name: row.name })}
        />
      ),
    },
  ];

  return (
    <>
      <p className="mb-4 text-body-sm text-fg-secondary">{t('automations.description')}</p>
      <DataTable
        rows={list.data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('automations.empty')}</p>}
        actionsHeader={tCommon('table.actions')}
        rowActions={(row) => (
          <Button
            size="sm"
            variant="secondary"
            disabled={!canManage}
            onClick={() => {
              setConfigureTarget(row);
              setConfigureOpen(true);
            }}
          >
            {t('automations.configure')}
          </Button>
        )}
      />

      <AutomationDialog
        open={configureOpen}
        onOpenChange={(next) => {
          setConfigureOpen(next);
          if (!next) setConfigureTarget(null);
        }}
        automation={configureTarget}
      />
    </>
  );
};
